import React, { useState } from "react";
import { useSaveTransactionMutation } from "../api/starterKitApi";
import { useCenter } from "../../peoples/centers/hooks/useCenter";
import { showToast } from "../../../core/utils/alert";
import { getApiErrorMessage } from "../../../core/utils/errorHandler";
import { useCourse } from "../../configuration/course/hooks/useCourse";
import CommonSelect from "../../../core/common/commonSelect";

interface TransferStockModalProps {
  initialData?: {
    center_id: string;
    center_name: string;
  } | null;
  onClose?: () => void;
}

const TransferStockModal = ({ initialData, onClose }: TransferStockModalProps) => {
  const [formData, setFormData] = useState({
    course_id: "",
    quantity: 0,
    to_center_id: "",
    from_main_center: true,
    type: "assigned_to_center" as const,
  });

  React.useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        to_center_id: initialData.center_id,
      }));
    }
  }, [initialData]);

  const { useCenterList } = useCenter({ skip: true });
  const { data: centerResponse } = useCenterList();
  const centers = centerResponse?.data?.items || [];
  const [saveTransaction, { isLoading }] = useSaveTransactionMutation();

  const centerOptions = centers.map((center) => ({
    value: center.id.toString(),
    label: center.name,
  }));

  const { getCourseState } = useCourse();
  const { courses } = getCourseState();
  const courseOptions = courses.map(course => ({
    value: course.id.toString(),
    label: course.name,
  }));

  const handleModalClose = () => {
    const modalElement = document.getElementById("transfer_stock");
    if (modalElement) {
      const bootstrap = (window as any).bootstrap;
      if (bootstrap && bootstrap.Modal) {
        const modal = bootstrap.Modal.getInstance(modalElement);
        modal?.hide();
      } else {
        const closeButton = modalElement.querySelector('[data-bs-dismiss="modal"]') as HTMLButtonElement;
        closeButton?.click();
      }
    }
    if (onClose) onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.to_center_id) {
      showToast({ title: "Error", text: "Please select a destination center", icon: "error" });
      return;
    }
    if (!formData.course_id) {
      showToast({ title: "Error", text: "Please select a course", icon: "error" });
      return;
    }

    try {
      const response = await saveTransaction({
        ...formData,
        course_id: Number(formData.course_id),
        to_center_id: Number(formData.to_center_id),
      }).unwrap();

      if (response.success) {
        showToast({
          title: "Success",
          text: "Stock transferred successfully",
          icon: "success",
        });
        // Reset form
        setFormData({
          course_id: "",
          quantity: 0,
          to_center_id: "",
          from_main_center: true,
          type: "assigned_to_center",
        });
        // Close modal
        handleModalClose();
      }
    } catch (error: any) {
      console.error("Transfer error:", error);
      showToast({
        title: "Error",
        text: getApiErrorMessage(error) || "Failed to transfer stock",
        icon: "error",
      });
    }
  };

  return (
    <div className="modal fade" id="transfer_stock">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">Transfer Stock to Center</h4>
            <button
              type="button"
              className="btn-close custom-btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={onClose}
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                <div className="col-12">
                  <div className="mb-3">
                    <label className="form-label">Course</label>
                    <CommonSelect
                      options={courseOptions}
                      value={courseOptions.find(opt => opt.value === formData.course_id)}
                      onValueChange={(val) => setFormData({ ...formData, course_id: val })}
                      placeholder="Select Course"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Destination Center</label>
                    <CommonSelect
                      options={centerOptions}
                      value={centerOptions.find(opt => opt.value === formData.to_center_id)}
                      onValueChange={(val) => setFormData({ ...formData, to_center_id: val })}
                      placeholder="Select Center"
                      required
                      isDisabled={!!initialData}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Quantity</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.quantity || ""}
                      onChange={(e) => setFormData({
                        ...formData,
                        quantity: Number(e.target.value)
                      })}
                      required
                      min="1"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? "Transferring..." : "Transfer Stock"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TransferStockModal;
